import { useTranslation } from '@mukil/i18n';
import { Button } from '@jumpstone/ui-react';
import { is_actionable, expense_action_error_key } from './expense_utils';
import { ReimbursementBadge } from './expense_reimbursement_badge';
import { format_date, format_money } from '../../lib/format';
import type { Expense } from '../../data/types';
import type { ExpenseActionFailure } from '../../data/use_expenses';

/** Props shared by the desktop `<tr>` row and the phone card -- one row's data plus its own approve/reject affordances and any action failure. */
interface ExpenseRowProps {
  expense: Expense;
  locale: string;
  /** `true` only when the caller both holds a write-capable grant AND ranks `expenses_role: approver`+ — see `use_expenses.ts`. */
  can_act: boolean;
  is_approving: boolean;
  action_failure: ExpenseActionFailure | undefined;
  on_approve: () => void;
  on_reject_click: () => void;
}

/** One expense-claim queue row: the claim's data plus its own approve/reject affordances and any action failure. */
export function ExpensesTableRow({
  expense,
  locale,
  can_act,
  is_approving,
  action_failure,
  on_approve,
  on_reject_click,
}: ExpenseRowProps): React.JSX.Element {
  const { t } = useTranslation('wp');

  return (
    <tr>
      <td className="font-semibold">
        {expense.employee_name ?? t('expenses.unknown_submitter')}
      </td>
      <td className="mono">{expense.claim_number}</td>
      <td className="text-end num">
        {format_money(expense.amount, expense.currency, locale)}
      </td>
      <td>{format_date(expense.submitted_on, locale)}</td>
      <td>
        <ReimbursementBadge reimbursement={expense.reimbursement} />
      </td>
      <td className="text-end whitespace-nowrap">
        {is_actionable(expense) && can_act && (
          <>
            <Button
              variant="ghost"
              size="xs"
              loading={is_approving}
              onClick={on_approve}
            >
              {t('common.approve')}
            </Button>
            <Button variant="ghost-danger" size="xs" onClick={on_reject_click}>
              {t('common.reject')}
            </Button>
          </>
        )}
        {action_failure !== undefined && (
          <p className="nv-help is-error mt-1" role="alert">
            {t(expense_action_error_key(action_failure))}
          </p>
        )}
      </td>
    </tr>
  );
}

/**
 * Phone card presentation of the same row (#23603, epic W2b) -- used by
 * `ResponsiveDataTable`'s `mobileCard` below `md`. The desktop `<tr>`'s last
 * `<td>` (approve/reject actions + the inline action-failure paragraph) is
 * off-screen behind horizontal scroll at 360px; here both render directly in
 * the card body so neither the actions nor a failure message require
 * panning to see. `claim_number` gets `break-all` -- unlike the desktop
 * `.mono` cell (which can overflow the scrollable table), the card has a
 * fixed width and a long reference number must wrap rather than clip.
 */
export function ExpenseMobileCard({
  expense,
  locale,
  can_act,
  is_approving,
  action_failure,
  on_approve,
  on_reject_click,
}: ExpenseRowProps): React.JSX.Element {
  const { t } = useTranslation('wp');

  return (
    <div className="p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold truncate">
            {expense.employee_name ?? t('expenses.unknown_submitter')}
          </div>
          <div className="mono text-[12px] text-[color:var(--ink-muted)] break-all">
            {expense.claim_number}
          </div>
        </div>
        <span className="num font-semibold text-end shrink-0">
          {format_money(expense.amount, expense.currency, locale)}
        </span>
      </div>
      <div className="flex items-center justify-between text-[12.5px] text-[color:var(--ink-muted)]">
        <span>{format_date(expense.submitted_on, locale)}</span>
        <ReimbursementBadge reimbursement={expense.reimbursement} />
      </div>
      {is_actionable(expense) && can_act && (
        <div className="flex gap-2 justify-end pt-1">
          <Button
            variant="ghost"
            size="sm"
            loading={is_approving}
            onClick={on_approve}
          >
            {t('common.approve')}
          </Button>
          <Button variant="ghost-danger" size="sm" onClick={on_reject_click}>
            {t('common.reject')}
          </Button>
        </div>
      )}
      {action_failure !== undefined && (
        <p className="nv-help is-error" role="alert">
          {t(expense_action_error_key(action_failure))}
        </p>
      )}
    </div>
  );
}
